import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FUNDS_ACCOUNT_WRITE_PERM } from '@mlm/shared';
import {
  type FundsAccountKindValue,
  apiErrorPayload,
  createFundsMapping,
  deleteFundsMapping,
  getTenantFeatures,
  listFundsAccounts,
  listFundsMappings,
  updateFundsMapping,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';

/**
 * "Posts to" — an inline Cash & Bank affordance for the payment/payout method
 * editors. Shows (and, under `funds_account.write`, sets) the company-wide
 * auto-posting rule for one (source, method code): the (source, code,
 * branch: ANY) wildcard row in `funds_account_mappings`. Writes are immediate —
 * independent of the host editor's own draft Save button. Per-branch rules are
 * only surfaced as a count; they're managed on Cash & Bank → Auto-posting
 * rules.
 *
 * Renders NOTHING when accounting is off or the viewer lacks `view.accounting`
 * — the host editors must look exactly as before.
 */

const KIND_LABEL: Record<FundsAccountKindValue, string> = {
  cash: 'Cash',
  bank: 'Bank',
  cheque: 'Cheque',
  ewallet: 'E-wallet',
};

/** Server slugs handled inline; everything else falls to the default toast. */
const FRIENDLY: Record<string, string> = {
  mapping_exists:
    'A rule for this code already exists — manage it on Cash & Bank → Auto-posting rules.',
  funds_account_not_found: 'That account no longer exists — pick another.',
  funds_account_inactive: 'That account is inactive. Reactivate it or pick another.',
  funds_account_branch_mismatch:
    'A method-wide rule can only post to a company-wide account, not a branch-bound one.',
  branchless_source_requires_branchless_account:
    'This source carries no branch, so the rule needs a company-wide account.',
  branchless_source_requires_wildcard_branch:
    "This source carries no branch, so the rule can't name one.",
  accounting_disabled: 'Accounting is turned off for this tenant.',
  not_found: 'That rule was removed by another admin — the list has been refreshed.',
};
const SILENT_CODES = Object.keys(FRIENDLY);

/**
 * Whether the "Posts to" affordance (and any host-page copy about it) should
 * render at all: accounting on + `view.accounting`.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the gate hook belongs beside the component so host pages can't drift from it
export function useFundsPostsToVisible(): boolean {
  const features = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const canView = useHasPerm('view.accounting');
  return Boolean(features.data?.accounting_enabled) && canView;
}

export function FundsPostsTo({
  source,
  code,
  label,
  saved = true,
}: {
  source: string;
  code: string;
  label?: string;
  /**
   * Whether this code exists in the SAVED registry. A draft code (new method,
   * or a rename not yet saved) must not write a rule — a wildcard row keyed on
   * a never-persisted code would match nothing, forever.
   */
  saved?: boolean;
}) {
  const qc = useQueryClient();
  const visible = useFundsPostsToVisible();
  const canWrite = useHasPerm(FUNDS_ACCOUNT_WRITE_PERM);

  const mappingsQ = useQuery({
    queryKey: ['funds-mappings'],
    queryFn: () => listFundsMappings(),
    enabled: visible,
  });
  const accountsQ = useQuery({
    queryKey: ['funds-accounts'],
    queryFn: () => listFundsAccounts(),
    enabled: visible,
  });

  const trimmedCode = code.trim();
  const rows = mappingsQ.data?.rows ?? [];
  // The wildcard row is found regardless of `active`: an inactive row still
  // OWNS the (source, code, ANY) unique key, so re-pointing must PATCH it (and
  // re-activate) rather than create-and-409. Display, though, mirrors the
  // resolver, which only matches active rules — an inactive rule posts nothing
  // and must read as "Not tracked".
  const wildcardRow = rows.find(
    (r) => r.source === source && r.code === trimmedCode && r.branchId === null,
  );
  const activeRule = wildcardRow?.active ? wildcardRow : undefined;
  const branchRules = rows.filter(
    (r) => r.source === source && r.code === trimmedCode && r.branchId !== null && r.active,
  ).length;
  // The server rejects an any-branch rule pointing at a branch-bound account,
  // so only company-wide (branchless) active accounts are offered.
  const eligible = (accountsQ.data?.rows ?? []).filter((a) => a.active && a.branchId === null);

  const save = useMutation({
    mutationFn: async (nextAccountId: string) => {
      if (!nextAccountId) {
        if (wildcardRow) await deleteFundsMapping(wildcardRow.id);
        return;
      }
      if (wildcardRow) {
        await updateFundsMapping(wildcardRow.id, { fundsAccountId: nextAccountId, active: true });
        return;
      }
      await createFundsMapping({
        source,
        code: trimmedCode,
        branchId: null,
        fundsAccountId: nextAccountId,
        memo: null,
      });
    },
    meta: { successMessage: 'Posting rule saved.', silentErrorCodes: SILENT_CODES },
    // Settled, not success: a 404 means another admin removed the rule, and
    // the list must resync rather than keep offering a row that is gone.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['funds-mappings'] });
      void qc.invalidateQueries({ queryKey: ['funds-accounts'] });
    },
  });

  if (!visible) return null;
  if (!trimmedCode) return <span className="text-sm text-muted-foreground">&mdash;</span>;
  if (!saved) {
    return (
      <p className="text-sm text-muted-foreground">
        Save the method first — rules key on the saved code.
      </p>
    );
  }

  const errCode = apiErrorPayload(save.error)?.error;
  const friendlyErr = errCode ? FRIENDLY[errCode] : undefined;
  const branchNote =
    branchRules > 0 ? `+${branchRules} per-branch rule${branchRules === 1 ? '' : 's'}` : null;

  if (!canWrite) {
    return (
      <div>
        {activeRule ? (
          <span className="text-sm">{activeRule.accountName}</span>
        ) : (
          <span className="text-sm text-muted-foreground">Not tracked</span>
        )}
        {branchNote && (
          <span className="ml-1.5 text-[11px] text-muted-foreground">{branchNote}</span>
        )}
      </div>
    );
  }

  // Keep a vanished target (deactivated / branch-bound since) VISIBLE so the
  // picker never shows a silently-wrong blank — but not re-pickable: it is no
  // longer an eligible destination.
  const current = activeRule?.fundsAccountId ?? '';
  const currentMissing = !!activeRule && !eligible.some((a) => a.id === current);

  return (
    <div>
      <select
        className="h-8 w-full rounded-md border bg-background px-2 text-sm"
        value={current}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.value)}
      >
        <option value="">Not tracked</option>
        {currentMissing && activeRule && (
          <option value={activeRule.fundsAccountId} disabled>
            {activeRule.accountName}
          </option>
        )}
        {eligible.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} ({KIND_LABEL[a.kind]})
          </option>
        ))}
      </select>
      {friendlyErr && <p className="mt-1 text-xs text-destructive">{friendlyErr}</p>}
      {(label || branchNote || eligible.length === 0) && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          {label && <>{label}. </>}
          {branchNote && <>{branchNote}. </>}
          {eligible.length === 0 && <>No company-wide account — register one on Cash &amp; Bank.</>}
        </p>
      )}
    </div>
  );
}
